/**
 * Reading preference (FTR00375): a person's own text size and contrast.
 *
 * docs/frontend-accessibility-text-contrast-proposal.md in the SVN repo. Ruled 2026-10-16:
 * the default look does not change for anyone; every change is a personal preference,
 * stored in this browser for the signed-in person only (another device starts at Default),
 * with nothing sent to the server.
 *
 * The page reads it through classes on <html>: `fts-text--large` / `fts-text--larger` and
 * `fts-contrast--high` (assets/scss/fts-readability.scss). No class is Default, which is
 * today's look. index.html puts the `last` mirror's classes on <html> before first paint, so
 * a reload never flashes small grey text first; the shell then applies the signed-in
 * person's own key.
 *
 * Pure apart from the storage, <html> and matchMedia it is handed (all injectable), so it
 * imports no store and can be unit tested on its own.
 */

// Where the Reading block sits on the release ladder (settingsStage.js stageAllows): Alpha
// 2026-10-16 (3c2f3449), Beta the same day (1a8cf1ee), GA 2026-10-19. GA because it changes
// nothing until a person chooses it, and because the workspace Beta Mode switch a pilot would
// have used is not stored by either backend, so no workspace could ever have been let in.
// Anything that is not a ReleaseStage is GA to stageAllows.
export const READING_STAGE = "GA";

export const TEXT_SIZES = Object.freeze(['default', 'large', 'larger']);
export const CONTRASTS = Object.freeze(['default', 'high']);
export const DEFAULT_DISPLAY = Object.freeze({ textSize: 'default', contrast: 'default', followDevice: false });

// `user:` keeps a person literally named "last" off the mirror key.
export const DISPLAY_USER_PREFIX = 'fetias-display:user:';
export const DISPLAY_LAST_KEY = 'fetias-display:last';
export const ROOT_CLASSES = Object.freeze(['fts-text--large', 'fts-text--larger', 'fts-contrast--high']);
export const MORE_CONTRAST_QUERY = '(prefers-contrast: more)';

/** Whitelist a stored value: anything unknown falls back to Default. */
export function normalizeDisplay(value) {
  const v = value && typeof value === 'object' ? value : {};
  return {
    textSize: TEXT_SIZES.indexOf(v.textSize) > -1 ? v.textSize : DEFAULT_DISPLAY.textSize,
    contrast: CONTRASTS.indexOf(v.contrast) > -1 ? v.contrast : DEFAULT_DISPLAY.contrast,
    followDevice: v.followDevice === true,
  };
}

/**
 * True when the person moved anything off Default. The Reading block stays reachable for them
 * even after they leave the stage (a pilot workspace switching Beta Mode off), so a choice
 * that is still being applied can always be changed back.
 */
export function isCustomised(value) {
  const d = normalizeDisplay(value);
  return d.textSize !== DEFAULT_DISPLAY.textSize || d.contrast !== DEFAULT_DISPLAY.contrast || d.followDevice;
}

/** The storage key for one person, or null when nobody is signed in. */
export function displayKey(userName) {
  return userName ? DISPLAY_USER_PREFIX + String(userName).trim().toLowerCase() : null;
}

function storageOf(storage) {
  if (storage) return storage;
  try {
    return window.localStorage;
  } catch (e) {
    return null; // blocked site data: the preference simply does not persist
  }
}

function readJson(storage, key) {
  try {
    const raw = storage && storage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

function writeJson(storage, key, value) {
  try {
    if (storage) storage.setItem(key, JSON.stringify(value));
  } catch (e) {
    // private window or full storage: the page still shows the choice, it just will not stick
  }
}

/** This person's preference on this device (Default when there is none). */
export function readDisplay(userName, storage) {
  const key = displayKey(userName);
  return normalizeDisplay(key ? readJson(storageOf(storage), key) : null);
}

/** Save this person's preference on this device. Returns what was stored. */
export function writeDisplay(userName, value, storage) {
  const normalized = normalizeDisplay(value);
  const key = displayKey(userName);
  if (key) writeJson(storageOf(storage), key, normalized);
  return normalized;
}

/**
 * What the page should show. Device contrast only counts while the person has switched
 * "Follow my device" on - by ruling nothing changes until they choose (A-7).
 */
export function resolveDisplay(stored, prefersMoreContrast) {
  const s = normalizeDisplay(stored);
  const high = s.contrast === 'high' || (s.followDevice && prefersMoreContrast === true);
  return { textSize: s.textSize, contrast: high ? 'high' : 'default' };
}

/** The <html> classes for a resolved preference; [] is Default. */
export function displayClasses(resolved) {
  const r = resolved || {};
  const classes = [];
  if (r.textSize === 'large' || r.textSize === 'larger') classes.push('fts-text--' + r.textSize);
  if (r.contrast === 'high') classes.push('fts-contrast--high');
  return classes;
}

function setRootClasses(root, classes) {
  if (!root || !root.classList) return;
  ROOT_CLASSES.forEach((name) => {
    if (classes.indexOf(name) > -1) root.classList.add(name);
    else root.classList.remove(name);
  });
}

function defaultRoot() {
  return typeof document !== 'undefined' ? document.documentElement : null;
}

function defaultMatchMedia() {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? (query) => window.matchMedia(query)
    : null;
}

// One device-contrast listener at a time: re-applying (another person, a changed
// choice) replaces it, sign-out removes it.
let following = null;

function stopFollowing() {
  if (!following) return;
  const { mql, listener } = following;
  if (typeof mql.removeEventListener === 'function') mql.removeEventListener('change', listener);
  else if (typeof mql.removeListener === 'function') mql.removeListener(listener);
  following = null;
}

/**
 * Paint the signed-in person's preference onto <html> and remember the classes as the
 * pre-paint mirror. Call at shell boot, when the person changes, and after a change in
 * Preferences. Returns the resolved { textSize, contrast }.
 *
 * @param {string|null} userName  authorizationStore.userName; null shows Default
 * @param {object} [options]      { storage, root, matchMedia } for tests
 */
export function applyDisplay(userName, options) {
  const opts = options || {};
  const storage = storageOf(opts.storage);
  const root = opts.root || defaultRoot();
  const matchMedia = opts.matchMedia || defaultMatchMedia();
  const stored = readDisplay(userName, storage);

  stopFollowing();
  let mql = null;
  if (stored.followDevice && matchMedia) {
    try {
      mql = matchMedia(MORE_CONTRAST_QUERY);
    } catch (e) {
      mql = null;
    }
  }

  const paint = () => {
    const resolved = resolveDisplay(stored, Boolean(mql && mql.matches));
    const classes = displayClasses(resolved);
    setRootClasses(root, classes);
    writeJson(storage, DISPLAY_LAST_KEY, classes);
    return resolved;
  };

  if (mql) {
    if (typeof mql.addEventListener === 'function') mql.addEventListener('change', paint);
    else if (typeof mql.addListener === 'function') mql.addListener(paint);
    following = { mql, listener: paint };
  }
  return paint();
}

/** Sign-out: the login page and the next person start at Default. Their saved choice stays. */
export function resetDisplayOnSignOut(options) {
  const opts = options || {};
  stopFollowing();
  setRootClasses(opts.root || defaultRoot(), []);
  const storage = storageOf(opts.storage);
  try {
    if (storage) storage.removeItem(DISPLAY_LAST_KEY);
  } catch (e) {
    // nothing to clean up
  }
}

export default {
  READING_STAGE,
  TEXT_SIZES,
  CONTRASTS,
  DEFAULT_DISPLAY,
  normalizeDisplay,
  isCustomised,
  displayKey,
  readDisplay,
  writeDisplay,
  resolveDisplay,
  displayClasses,
  applyDisplay,
  resetDisplayOnSignOut,
};
